import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import { useGetProjectsQuery } from '../api/projectsApi';
import { selectCurrentProject, setCurrentProject, clearCurrentProject } from '../store/projectsSlice';

// Selection belongs to the workspace lifecycle, not a conditionally mounted menu.
export function useWorkspaceProject() {
  const dispatch = useAppDispatch();
  const currentProject = useAppSelector(selectCurrentProject);
  const result = useGetProjectsQuery();
  const { currentData: projects, isSuccess: projectsLoaded, isFetching: projectsRefreshing } = result;
  const activeProjects = useMemo(() => projects?.filter((project) => project.status === 'active') ?? [], [projects]);

  // The server-side, tenant-filtered list is authoritative. Restore a usable
  // workspace automatically after login, while never carrying a project
  // selection across organizations.
  useEffect(() => {
    // Creating a project invalidates the list. RTK Query retains the previous
    // (empty) result while the refreshed list is in flight, so treating that
    // stale snapshot as authoritative clears the project we just selected.
    if (!projectsLoaded || projectsRefreshing) return;

    if (currentProject && activeProjects.some((project) => project.uuid === currentProject.uuid)) {
      return;
    }

    if (activeProjects.length > 0) {
      dispatch(setCurrentProject(activeProjects[0]));
    } else if (currentProject) {
      dispatch(clearCurrentProject());
    }
  }, [activeProjects, currentProject, dispatch, projectsLoaded, projectsRefreshing]);

  return {
    ...result,
    currentProject,
    isResolving: !currentProject && (!projectsLoaded || projectsRefreshing || activeProjects.length > 0),
  };
}
